import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/shell/PageHeader";
import { TableSkeleton } from "@/components/common/primitives";
import { api, unwrapEnvelope } from "@/lib/api";
import { SETTINGS_TABS } from "@/lib/settings/constants";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { IntegrationsPanel } from "@/components/settings/IntegrationsPanel";
import {
  BillingPanel,
  DevicesPanel,
  PreferencesPanel,
  UsagePanel,
} from "@/components/settings/SettingsPanels";
import "@/components/settings/settings.css";

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = SETTINGS_TABS.includes(requestedTab)
    ? requestedTab
    : "integrations";

  const [integrations, setIntegrations] = useState(null);
  const [billing, setBilling] = useState(null);
  const [usage, setUsage] = useState(null);
  const [devices, setDevices] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const setTab = useCallback(
    (tab) => {
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams]
  );

  const loadIntegrations = useCallback(async () => {
    const res = await api.integrationsList();
    setIntegrations(unwrapEnvelope(res)?.integrations || []);
  }, []);

  const loadCore = useCallback(async () => {
    const [billingRes, usageRes, prefsRes] = await Promise.all([
      api.billingSummary(),
      api.usage(),
      api.accountPreferences(),
    ]);
    setBilling(unwrapEnvelope(billingRes));
    setUsage(unwrapEnvelope(usageRes));
    const prefBundle = unwrapEnvelope(prefsRes);
    const profileRes = await api.accountProfile();
    const profileBundle = unwrapEnvelope(profileRes);
    setPreferences({
      ...(prefBundle?.preferences || {}),
      default_sourcing_postal_code:
        profileBundle?.profile?.default_sourcing_postal_code || "",
    });
  }, []);

  const loadDevices = useCallback(async () => {
    const res = await api.accountDevices();
    setDevices(unwrapEnvelope(res)?.devices || []);
  }, []);

  useEffect(() => {
    loadIntegrations().catch(() => toast.error("Could not load integrations"));
    loadCore().catch(() => toast.error("Could not load settings"));
  }, [loadIntegrations, loadCore]);

  useEffect(() => {
    if (activeTab === "devices") {
      loadDevices().catch(() => toast.error("Could not load devices"));
    }
  }, [activeTab, loadDevices]);

  async function savePreferences(form) {
    setSavingPrefs(true);
    try {
      await api.patchAccountPreferences(form);
      toast.success("Preferences saved");
      await loadCore();
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSavingPrefs(false);
    }
  }

  const loading =
    activeTab === "integrations"
      ? integrations == null
      : activeTab === "devices"
        ? devices == null
        : activeTab === "preferences"
          ? preferences == null
          : billing == null || usage == null;

  return (
    <div className="settings-page space-y-4">
      <PageHeader
        title="Settings"
        description="Integrations, billing, usage, devices and workspace preferences."
      />
      <SettingsTabs activeTab={activeTab} onChange={setTab} />

      {loading ? (
        <TableSkeleton rows={4} />
      ) : (
        <>
          {activeTab === "integrations" && (
            <IntegrationsPanel items={integrations} onRefresh={loadIntegrations} />
          )}
          {activeTab === "billing" && (
            <BillingPanel billing={billing} onBillingUpdated={loadCore} />
          )}
          {activeTab === "usage" && <UsagePanel usage={usage} />}
          {activeTab === "devices" && (
            <DevicesPanel
              devices={devices}
              onRevoke={async (id) => {
                await api.revokeDevice(id);
                loadDevices();
              }}
              onRevokeOthers={async () => {
                await api.revokeOtherDevices();
                loadDevices();
              }}
            />
          )}
          {activeTab === "preferences" && (
            <PreferencesPanel
              preferences={preferences}
              saving={savingPrefs}
              onSave={savePreferences}
            />
          )}
        </>
      )}
    </div>
  );
}
